/**
 * @author michalstolarski
 * @since 2023-9-12
 */

import * as React from "react";
import styled, { useTheme, css } from "styled-components";

import { rem } from "@ui/helpers";
import {
  CaretRight,
  CheckmarkBigCircle,
  TrashDeleteBin2Circle,
} from "@ui/Assets/MyIcons";
import { TestWrapper } from "@ui/Atoms/TestWrapper";
import { Button } from "@ui/Atoms/Button";
import {
  PaymentMethodDetailsProps,
  PaymentMethodDetails,
} from "@ui/Molecules/PaymentMethodDetails";

const maxDistance = 200;

interface Translations {
  expiresText: string;
  defaultText: string;
  removeText: string;
  cardExpiredText: string;
}

interface Props
  extends Omit<
    PaymentMethodDetailsProps,
    "testId" | "className" | "translations"
  > {
  testId?: string;
  className?: string;
  uuid: string;
  translations: Translations;
  onDetailsClick: (uuid: string) => void;
  onSetAsDefault: (uuid: string) => void;
  onRemove: (uuid: string) => void;
  isSelected?: boolean;
}

const PaymentMethodCard = (props: Props): React.ReactElement => {
  const theme = useTheme();
  const detailsButtonRef = React.useRef<HTMLButtonElement>(null);
  const buttonsWrapperRef = React.useRef<HTMLDivElement>(null);

  const [translateX, setTranslateX] = React.useState(0);
  const [distance, setDistance] = React.useState(0);

  /**
   * Calculate how far should the details button be translated
   * This animation shows delete and set as default buttons
   */
  React.useEffect(() => {
    if (!buttonsWrapperRef.current) return;
    if (!props.isSelected) {
      setTranslateX(0);
      return;
    }

    const { width } = buttonsWrapperRef.current.getBoundingClientRect();

    setTranslateX(width * -1);
  }, [buttonsWrapperRef.current, props.isSelected]);

  /**
   * Calculate how far should the details be translated to the right
   * This animation prevent payment method details from being cut off from the screen
   */
  React.useEffect(() => {
    if (!detailsButtonRef.current) return;
    if (!props.isSelected) {
      setDistance(0);
      return;
    }

    const $children = detailsButtonRef.current.children;
    const diff = Math.floor(
      $children[1].getBoundingClientRect().left -
        $children[0].getBoundingClientRect().right,
    );

    setDistance(diff < maxDistance ? diff : maxDistance);
  }, [detailsButtonRef.current, props.isSelected]);

  const handleDetailsClick = () => {
    props.onDetailsClick(props.uuid);
  };
  const handleSetAsDefault = () => {
    props.onSetAsDefault(props.uuid);
  };
  const handleRemove = () => {
    props.onRemove(props.uuid);
  };

  return (
    <TestWrapper testId={props.testId}>
      <Container className={props.className}>
        <DetailsButton
          ref={detailsButtonRef}
          type="button"
          onClick={handleDetailsClick}
          translateX={translateX}
        >
          <AliasDetails
            type={props.type}
            maskedNumber={props.maskedNumber}
            expirationDate={props.expirationDate}
            paymentMethodStatus={props.paymentMethodStatus}
            translations={{
              expiresText: props.translations.expiresText,
              cardExpiredText: props.translations.cardExpiredText,
            }}
            distance={distance}
          />

          <TestWrapper
            testId={props.testId ? `${props.testId}_openActions` : undefined}
          >
            <div>
              <CaretRight width={theme.icons.sizes.base_x4} />
            </div>
          </TestWrapper>
        </DetailsButton>

        <ButtonsWrapper ref={buttonsWrapperRef}>
          <TestWrapper
            testId={props.testId ? `${props.testId}_setAsDefault` : undefined}
          >
            <ActionButton
              type="button"
              variant="blue"
              size="fill"
              onClick={handleSetAsDefault}
            >
              <ActionButtonContent>
                <CheckmarkBigCircle width={theme.icons.sizes.base_x3} />
                <span>{props.translations.defaultText}</span>
              </ActionButtonContent>
            </ActionButton>
          </TestWrapper>
          <TestWrapper
            testId={props.testId ? `${props.testId}_remove` : undefined}
          >
            <ActionButton
              type="button"
              variant="red"
              size="fill"
              onClick={handleRemove}
            >
              <ActionButtonContent>
                <TrashDeleteBin2Circle width={theme.icons.sizes.base_x3} />
                <span>{props.translations.removeText}</span>
              </ActionButtonContent>
            </ActionButton>
          </TestWrapper>
        </ButtonsWrapper>
      </Container>
    </TestWrapper>
  );
};

export { Props as PaymentMethodCardProps, PaymentMethodCard };

const Container = styled.div`
  position: relative;
  height: ${rem(80)};
`;

const DetailsButton = styled.button<{ translateX?: number }>`
  all: unset;
  position: relative;
  width: 100%;
  height: 100%;
  background-color: ${props => props.theme.palette.white};
  transition: 300ms transform cubic-bezier(0.4, 0, 0.16, 1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${props => props.theme.margins.base_x2};
  cursor: pointer;
  z-index: 1;

  ${props =>
    !!props.translateX &&
    css`
      transform: translateX(
        calc(${props.translateX}px - ${props.theme.margins.base_x2})
      );
    `}
`;

const AliasDetails = styled(PaymentMethodDetails)<{ distance?: number }>`
  transition: 300ms transform cubic-bezier(0.4, 0, 0.16, 1);

  ${props =>
    !!props.distance &&
    css`
      transform: translateX(
        calc(${props.distance}px - ${props.theme.margins.base_x4})
      );
    `}
`;

const ButtonsWrapper = styled.div`
  position: absolute;
  top: 50%;
  right: 0;
  min-width: 50%;
  max-width: 66%;
  max-height: calc(100% - 4px);
  transform: translateY(-50%);
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.margins.base_x2};
  overflow: hidden;
`;

const ActionButton = styled(Button)`
  min-width: auto;
  min-height: auto;
  padding: ${props => props.theme.margins.base_x1_5}
    ${props => props.theme.margins.base_x2};
`;

const ActionButtonContent = styled.div`
  font-size: ${props => props.theme.fonts.sizes.caption};
  font-weight: ${props => props.theme.fonts.weights.semibold};
  line-height: ${props => props.theme.fonts.lineHeights.caption};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${props => props.theme.margins.half};
`;
